import { createSlice } from '@reduxjs/toolkit';
import { IActionType, IApiQueryListResponse, IApiQuerySingularResponse } from '../../utils';
import { TLoadedState } from '../../../types/TLoadedState';
import { THydraDynamicTypeKey, THydraDynamicTypesMap, THydraDynamicTypeTypeValue } from './THydraDynamicTypesMap';
import { Nullable, replaceAt } from '@jamesgmarks/utilities';
import { IApiQueryResponse } from '@llws/api-common';

type TDynamicStoreData = {
  [K in keyof THydraDynamicTypesMap]: {
    loadedState: TLoadedState,
    single: null | THydraDynamicTypesMap[K],
    list: null | THydraDynamicTypesMap[K][],
  }
}

export type TDynamicState = {
  data: TDynamicStoreData,
  metaData: Nullable<IApiQueryResponse<THydraDynamicTypeTypeValue>['meta']>,
};

// Type guard to check if an object has the 'id' property
const hasIdProperty = (obj: unknown): obj is { id: number } => {
  return typeof obj === 'object' && obj !== null && 'id' in obj;
};

// Type guard to check if an object has the 'subscriptionId' property
const hasSubscriptionIdProperty = (obj: unknown): obj is { subscriptionId: number } => {
  return typeof obj === 'object' && obj !== null && 'subscriptionId' in obj;
};

const updateDynamicState = <T extends keyof THydraDynamicTypesMap>(
  startState: TDynamicStoreData,
  resource: T,
  newState: Partial<{ single: THydraDynamicTypesMap[T] | null } & TDynamicStoreData[T]>,
) => {
  const listItemIndex = startState[resource]?.list?.findIndex(item => (
    (
      hasIdProperty(item)
      && newState.single
      && hasIdProperty(newState.single)
      && item.id === newState.single.id
    ) || (
      hasSubscriptionIdProperty(item)
      && newState.single
      && typeof newState.single === 'object'
      && hasSubscriptionIdProperty(newState.single)
      && item.subscriptionId === newState.single.subscriptionId
    ) || false
  )) ?? -1;

  const oldList = (startState[resource]?.list ?? []);

  const newList = newState.list ?? (
    listItemIndex >= 0 && newState.single
      ? replaceAt(oldList, listItemIndex, newState.single as THydraDynamicTypesMap[T])
      : oldList
  );

  return {
    ...startState,
    [resource]: {
      loadedState: newState?.loadedState ?? startState[resource]?.loadedState ?? 'idle',
      single: newState?.single ?? startState[resource]?.single ?? null,
      list: newList,
    },
  };
};

const updateDynamicListState = <T extends keyof THydraDynamicTypesMap>(
  startState: TDynamicStoreData,
  resource: T,
  newState: Partial<{} & TDynamicStoreData[T]>,
) => {
  const oldList = startState[resource]?.list ?? [];

  // Validate all new items exist in the old list
  const isEveryNewItemValid = newState.list?.reduce((isValid, newItem) => {
    const itemExists = oldList.some(oldItem =>
      (
        'id' in oldItem && 'id' in newItem
        && newItem.id && oldItem.id === newItem.id
      ) ||
      ('subscriptionId' in oldItem && 'subscriptionId' in newItem
        && newItem.subscriptionId && oldItem.subscriptionId === newItem.subscriptionId));
    return isValid && itemExists;
  }, true);

  if (!isEveryNewItemValid) {
    throw new Error(`One or more items do not exist in the current state. Failed to update the list`);
  }

  const updatedList = oldList.map(oldItem => {
    const newItem = newState.list?.find(newItem =>
      (
        'id' in newItem && 'id' in oldItem
        && newItem.id && newItem.id === oldItem.id
      ) ||
      (
        'subscriptionId' in oldItem && 'subscriptionId' in newItem
        && newItem.subscriptionId && newItem.subscriptionId === oldItem.subscriptionId
      ));
    return newItem ? { ...oldItem, ...newItem } : oldItem;
  });

  return {
    ...startState,
    [resource]: {
      ...startState[resource],
      list: updatedList,
    },
  };
};

export const dynamicHydraSlice = () => createSlice({
  name: 'dynamic',
  initialState: {
    data: {},
    metaData: null,
  } as TDynamicState,
  reducers: {
    setLoadedState: (state, action: IActionType<{
      resource: THydraDynamicTypeKey,
      newState: TLoadedState,
    }>) => {
      state.data = updateDynamicState(state.data, action.payload.resource, {
        loadedState: action.payload.newState,
      });
    },
    dynamicSingleReceived: (state, action: IActionType<{
      resource: THydraDynamicTypeKey
      response: IApiQuerySingularResponse<THydraDynamicTypesMap[THydraDynamicTypeKey]>
    }>) => {
      state.data = updateDynamicState(state.data, action.payload.resource, {
        single: action.payload.response.data as unknown as THydraDynamicTypesMap['subscriptions'], // This is cheating, but it works
      });
    },
    dynamicManyReceived: (state, action: IActionType<{
      resource: THydraDynamicTypeKey
      response: IApiQueryListResponse<THydraDynamicTypesMap[THydraDynamicTypeKey]>
    }>) => {
      state.data = updateDynamicState(state.data, action.payload.resource, {
        list: action.payload.response.data as unknown as THydraDynamicTypesMap['subscriptions'][], // This is cheating, but it works
      });
      state.metaData = { ...action.payload.response.meta };
    },

    dynamicManyUpdateReceived: (state, action: IActionType<{
      resource: THydraDynamicTypeKey
      response: IApiQueryListResponse<THydraDynamicTypesMap[THydraDynamicTypeKey]>
    }>) => {
      state.data = updateDynamicListState(state.data, action.payload.resource, {
        list: action.payload.response.data as unknown as THydraDynamicTypesMap['subscriptions'][], // This is cheating, but it works
      });
    },

    clearDynamicDataForResource: (state, action: IActionType<{ resource: THydraDynamicTypeKey }>) => {
      state.data[action.payload.resource] = {
        loadedState: 'idle',
        single: null,
        list: [],
      };
    },
  },
});

const slice = dynamicHydraSlice();

// Action creators are generated for each case reducer function
export const {
  setLoadedState,
  dynamicSingleReceived,
  dynamicManyReceived,
  dynamicManyUpdateReceived,
  clearDynamicDataForResource,
} = slice.actions;

export default slice.reducer;
